/* components/patterns/integrations/PosGuide.jsx — инструкция подключения учётной
   системы. Самодостаточный общий компонент (provider='iiko'|'syrve'|'rkeeper');
   живёт в самом окне интеграций (Настройки → Для бизнеса → Интеграции) и в
   онбординге. Реальные скриншоты экранов iiko Office лежат в
   public/pos-guide/ и подключаются через slide.image (Syrve — ребренд iiko,
   те же экраны). Слайды без снимка — только текст.

   ПОЧЕМУ НАБОР СЛАЙДОВ ЛЕЖИТ В РЕЕСТРЕ ФУНКЦИЕЙ, А НЕ ШАБЛОНОМ С ПОДСТАНОВКАМИ.
   У iiko/Syrve подключение — это работа пользователя в Office: завести
   API-сотрудника, выдать ему права, взять хост из адресной строки. У r_keeper
   такой работы нет вовсе: прямого HTTP-API нет, доступ к базе открывает дилер
   UCS отдельной услугой, а пользователь только переписывает реквизиты из письма.
   Это разные инструкции по сути, а не одна с другими словами; свести их в общий
   шаблон значило бы описывать r_keeper через несуществующие у него шаги.

   Теги прав — как в правах доступа сервера iiko; человеческие названия зеркалят
   backend/integrations/permissions.py CAPABILITIES (плюс общий пререквизит B_RPT).
   У r_keeper прав API-пользователя нет как понятия — слайда «Права» у него нет.
   Контент-контракт покрыт vitest (PosGuide.test.js). Стили —
   styles/integrations-shared.css (.posguide-*; .rights-tag — общий). */

import React from 'react';
import { Button } from '../../ui/Button.jsx';

  const { useState } = React;

  // Провайдер-специфика: тексты офиса/облака, пример адреса сервера и построитель
  // слайдов (`slides`). Просмотрщик ниже о провайдерах не знает вовсе — новая
  // система добавляется записью ЗДЕСЬ, без правки JSX.
  const POS_GUIDE_META = {
    iiko: {
      label: 'iiko',
      office: 'iiko Office',
      cloudDomain: 'iiko.it',
      hostExample: 'my-restoran.iiko.it',
      urlPlaceholder: 'например, my-restoran.iiko.it',
      slides: officeSlides,
    },
    syrve: {
      label: 'Syrve',
      office: 'Syrve Office',
      cloudDomain: 'syrve.online',
      hostExample: 'my-restaurant.syrve.online',
      urlPlaceholder: 'например, my-restaurant.syrve.online',
      slides: officeSlides,
    },
    rkeeper: {
      label: 'r_keeper',
      // Услуги дилера называются так ДОСЛОВНО — их и просят у партнёра UCS.
      // Первая открывает базу кассы (продажи), вторая — склад StoreHouse
      // (себестоимость). Это две разные заявки к одному и тому же дилеру.
      service: 'R-Keeper Cloud Интеграция SQL connect',
      storehouseService: 'Доступ до SH5 Web API',
      hostExample: 'sql.example.ru:1433',
      urlPlaceholder: 'например, sql.example.ru:1433',
      slides: sqlAccessSlides,
    },
  };

  // Права API-пользователя для аналитики (слайд «Права»). name — как в правах
  // доступа сервера, note — зачем нужно в Ресториуме.
  const POS_REQUIRED_RIGHTS = [
    { tag: 'B_RPT', name: 'Базовые отчёты', note: 'общий доступ к отчётам — обязательное' },
    { tag: 'B_CASR', name: 'Право просматривать отчёты по продажам', note: 'продажи и кассовые смены' },
    { tag: 'B_VOTR', name: 'Просматривать OLAP-отчёт по проводкам', note: 'нужно для ОПиУ' },
    { tag: 'B_VSR', name: 'Просматривать складские отчёты', note: 'остатки на складах' },
    { tag: 'B_STO', name: 'Работа с товарами и складами', note: 'справочник складов' },
    { tag: 'B_VN', name: 'Просмотр номенклатурных справочников', note: 'номенклатура' },
    { tag: 'B_INVC', name: 'Создавать и редактировать накладные', note: 'закупки (приходные накладные)' },
    { tag: 'B_PER', name: 'Работа с данными о персонале', note: 'опционально — для отчёта по официантам' },
  ];

  // Реальные скриншоты экранов iiko/Syrve Office (public/pos-guide/, отдаёт nginx
  // как статику). Кадры одинаковы для iiko и Syrve (тот же Office). Слот
  // slide.image рендерится как <img>; нет кадра → слайд текстовый.
  const SHOT = {
    employee: '/pos-guide/iiko-employee.png',
    credentials: '/pos-guide/iiko-credentials.png',
    rights: '/pos-guide/iiko-rights.png',
  };

  // Слайды систем на движке iiko (iiko / Syrve): офис → сотрудник → логин/пароль →
  // права → адрес → готово. m — запись реестра POS_GUIDE_META.
  function officeSlides(m) {
    return [
      {
        key: 'office',
        title: 'Войдите в ' + m.office + '/Chain',
        image: null,
        body: (
          <>
            <p className="posguide__p">
              Откройте {m.office} под административной учётной записью — понадобятся права
              на создание сотрудников и раздачу доступов.
            </p>
            <p className="posguide__p">
              Облачный сервер живёт по адресу вида{' '}
              <code className="posguide__code">https://&lt;ваш-сервер&gt;.{m.cloudDomain}/resto</code>
              {' '}(например, {m.hostExample}). Если система развёрнута на собственном сервере
              RMS — используйте его адрес.
            </p>
            <p className="posguide__p">
              У сети из нескольких заведений всё то же самое, но в центральном офисе
              сети ({m.label}Chain): сотрудника создаёте там, а в Ресториум вводите
              адрес центрального сервера — после проверки связи он предложит выбрать
              заведение.
            </p>
          </>
        ),
      },
      {
        key: 'employee',
        title: 'Создайте сотрудника для API',
        image: SHOT.employee,
        shotAlt: 'Персональная карточка сотрудника в iiko Office, вкладка «Основные сведения»',
        body: (
          <>
            <p className="posguide__p">
              В {m.office} откройте раздел «Сотрудники» → «Добавить». В «Персональной
              карточке» на вкладке «Основные сведения» задайте «Имя в системе» — например{' '}
              <code className="posguide__code">restorium_api</code> — и любую должность.
            </p>
            <p className="posguide__p">
              Отдельный сервисный пользователь безопаснее личной учётки: доступ можно
              отозвать в любой момент, не меняя свой пароль. Логин и пароль зададите на
              следующем шаге.
            </p>
          </>
        ),
      },
      {
        key: 'credentials',
        title: 'Задайте логин и пароль',
        image: SHOT.credentials,
        shotAlt: 'Вкладка «Дополнительные сведения»: поля «Имя входа в систему» и «Пароль»',
        body: (
          <>
            <p className="posguide__p">
              На вкладке «Дополнительные сведения» заполните «Имя входа в систему»
              (это <b>логин</b>) и «Пароль». <b>Именно эти логин и пароль вы введёте в
              Ресториум</b> на последнем шаге.
            </p>
            <p className="posguide__p">
              Поле «Пароль внешнего API» трогать не нужно — Ресториум подключается по
              обычному логину и паролю сотрудника.
            </p>
          </>
        ),
      },
      {
        key: 'rights',
        title: 'Выдайте права',
        image: SHOT.rights,
        shotAlt: 'Экран «Права доступа» в iiko Office со списком прав',
        body: (
          <>
            <p className="posguide__p">
              Откройте «Сотрудники → Права доступа», найдите созданного сотрудника
              (или его роль) и включите права:
            </p>
            <ul className="posguide__rights">
              {POS_REQUIRED_RIGHTS.map((r) => (
                <li key={r.tag} className="posguide__right">
                  <span className="rights-tag">{r.tag}</span>
                  <span className="posguide__right-text">{r.name} — {r.note}</span>
                </li>
              ))}
            </ul>
            <p className="posguide__p">
              Права ищите по коду в правой панели (поиск внизу). Чего-то не хватит — не
              страшно: после подключения Ресториум сам проверит права и подскажет, какие
              нужно довыдать.
            </p>
          </>
        ),
      },
      {
        key: 'server',
        title: 'Адрес сервера',
        image: null,
        body: (
          <>
            <p className="posguide__p">
              Ресториум подключается по адресу вида{' '}
              <code className="posguide__code">https://&lt;хост&gt;:443/resto</code> — от вас
              нужен только хост.
            </p>
            <p className="posguide__p">
              Хост — это адрес вашего облака, например{' '}
              <code className="posguide__code">{m.hostExample}</code>. Проще всего взять его
              из адресной строки браузера, где открыт {m.office} — часть до{' '}
              <code className="posguide__code">/resto</code>.
            </p>
          </>
        ),
      },
      {
        key: 'ready',
        title: 'Готово — введите данные',
        image: null,
        body: (
          <>
            <p className="posguide__p">
              Осталось ввести адрес сервера, логин и пароль API-сотрудника в форму
              подключения. Ресториум проверит связь, права и запустит загрузку
              истории продаж — она пойдёт в фоне.
            </p>
          </>
        ),
      },
    ];
  }

  // Слайды систем, к которым подключаются напрямую к базе (r_keeper): доступ не
  // настраивается пользователем, а покупается у дилера. Три шага — ровно то, что
  // проверено на пилоте; больше «правдоподобных» шагов здесь быть не должно,
  // потому что каждый лишний уводит ресторатора не туда.
  function sqlAccessSlides(m) {
    return [
      {
        key: 'dealer',
        title: 'Закажите доступ у своего дилера',
        image: null,
        body: (
          <>
            <p className="posguide__p">
              У {m.label} нет облачного API, которое можно включить самому: Ресториум
              читает базу вашей кассы. Доступ к ней открывает дилер (партнёр UCS) —
              тот, кто обслуживает вашу кассу.
            </p>
            <p className="posguide__p">
              Услуга называется{' '}
              <code className="posguide__code">{m.service}</code> — назовите её
              дилеру дословно, так она заводится в заявке.
            </p>
          </>
        ),
      },
      {
        key: 'credentials',
        title: 'Реквизиты придут письмом от UCS',
        image: null,
        body: (
          <>
            <p className="posguide__p">
              В письме будут адрес SQL-инстанса (хост или{' '}
              <code className="posguide__code">хост:порт</code>), логин и пароль
              SQL-пользователя, иногда — имя базы. Их и нужно ввести в форму ниже.
            </p>
            <p className="posguide__p">
              Имя базы необязательное: пусто — Ресториум определит её сам (обычно она
              называется <code className="posguide__code">RK7</code>). Заполняйте
              только если в письме указано другое имя.
            </p>
          </>
        ),
      },
      // Слайд про склад стоит ДО «канала» и после реквизитов кассы: к этому
      // моменту человек уже понял, что заявка идёт дилеру, и вторая заявка
      // читается как продолжение той же, а не как отдельная затея. Раньше
      // ставить нельзя — сначала должны заработать продажи.
      {
        key: 'storehouse',
        title: 'Себестоимость: закажите доступ к StoreHouse',
        image: null,
        body: (
          <>
            <p className="posguide__p">
              Себестоимость касса не считает — калькуляция живёт в StoreHouse, это
              отдельная система того же вендора. Без неё продажи, выручка, средний чек
              и отчёт по официантам работают, а food cost, наценка и прибыль остаются
              пустыми.
            </p>
            <p className="posguide__p">
              Просить нужно у того же дилера и той же заявкой:{' '}
              <code className="posguide__code">{m.storehouseService}</code>. Доступ
              бесплатный — отдельная лицензия не покупается.
            </p>
            <p className="posguide__p">
              В ответ придут адрес вида{' '}
              <code className="posguide__code">хост:порт</code>, логин и пароль
              пользователя StoreHouse. Их вводят во второй блок формы ниже
              («Себестоимость: StoreHouse») — он необязательный, и заполнить его можно
              когда угодно, хоть через месяц после подключения кассы.
            </p>
            <p className="posguide__p">
              Просите пользователя <b>только на чтение</b>: сервис StoreHouse работает
              по HTTP, соединение с ним не шифруется. Ресториум ничего в складе не
              меняет — ему достаточно права смотреть отчёт о реализации.
            </p>
          </>
        ),
      },
      {
        key: 'channel',
        title: 'Подключение по защищённому каналу',
        image: null,
        body: (
          <>
            <p className="posguide__p">
              Облачный r_keeper пускает к базе только через VPN-канал: вместе с
              реквизитами UCS присылает файл конфигурации (<code className="posguide__code">.ovpn</code>).
              Загрузите его в поле «Файл конфигурации VPN» формы ниже — канал
              поднимется сам, публиковать SQL-порт кассы в интернет не нужно.
              У локальной установки r_keeper файла нет: доступ к серверу открывает
              ваш IT.
            </p>
            <p className="posguide__p">
              У StoreHouse иначе, и об этом стоит знать заранее: его сервис работает
              по обычному HTTP, то есть соединение с ним не шифруется. Поэтому для
              него и просят отдельного пользователя только на чтение.
            </p>
            <p className="posguide__p">
              Обе системы Ресториум только читает: он не меняет в них ни одной строки.
              Логины и пароли хранятся в зашифрованном виде и не отображаются.
            </p>
          </>
        ),
      },
    ];
  }

  // Слайды инструкции по коду системы. Незнакомое значение деградирует на iiko:
  // компонент рендерят и старые вызовы без provider.
  function getPosGuideSlides(provider) {
    const m = POS_GUIDE_META[provider] || POS_GUIDE_META.iiko;
    return m.slides(m);
  }

  // Просмотрщик слайдов. Точки-прогресс кликабельны; «Назад» с первого слайда зовёт
  // onBack (если задан), «Далее» с последнего — onFinish (finishLabel — его подпись).
  // БЕЗ onFinish (встраивание во вкладку интеграций) кнопки «Далее» на последнем
  // слайде нет — навигация точками/«Назад», форма подключения уже рядом.
  function PosGuide({ provider = 'iiko', initialSlide = 0, onBack, onFinish,
                      finishLabel = 'Далее' }) {
    const slides = getPosGuideSlides(provider);
    const clamp = (n) => Math.max(0, Math.min(slides.length - 1, Number(n) || 0));
    const [idx, setIdx] = useState(() => clamp(initialSlide));
    const slide = slides[idx];
    const last = idx === slides.length - 1;

    return (
      <div className="posguide">
        <p className="t-caption text-mute posguide__count">
          Шаг {idx + 1} из {slides.length}
        </p>
        <h3 className="t-h4 posguide__title">{slide.title}</h3>
        <div className="posguide__body">{slide.body}</div>
        {slide.image && (
          <img className="posguide__shot posguide__shot--photo" src={slide.image}
            alt={slide.shotAlt || ''} loading="lazy" />
        )}
        <div className="posguide__nav">
          <Button variant="ghost" iconLeft="arrow-left" disabled={idx === 0 && !onBack}
            onClick={() => (idx === 0 ? (onBack && onBack()) : setIdx(idx - 1))}>
            Назад
          </Button>
          <div className="posguide__dots">
            {slides.map((s, i) => (
              <button key={s.key} type="button"
                aria-label={'Слайд ' + (i + 1) + ': ' + s.title}
                aria-current={i === idx || undefined}
                className={'posguide__dot' + (i === idx ? ' is-active' : '')}
                onClick={() => setIdx(i)} />
            ))}
          </div>
          {(last && !onFinish)
            ? <span className="posguide__nav-spacer" aria-hidden="true" />
            : (
              <Button variant="primary" iconRight={last ? undefined : 'arrow-right'}
                onClick={() => (last ? (onFinish && onFinish()) : setIdx(idx + 1))}>
                {last ? finishLabel : 'Далее'}
              </Button>
            )}
        </div>
      </div>
    );
  }

export { POS_GUIDE_META, POS_REQUIRED_RIGHTS, SHOT, getPosGuideSlides, PosGuide };
